// Custom fields display and inline editing for task detail/edit pages.

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { useTask, useI18n } from '@react/hooks';
import {
  Card,
  CardContent,
  Badge,
} from '@react/components';
import { Select } from '@/components/Select';
import type { CustomFieldValue, CustomFieldDefinition } from '@core/types/task';
import { formatDateTime } from '@/features/task/logic';

interface CustomFieldsCardProps {
  taskId: string;
  editable?: boolean;
}

// Render a custom field value based on its type
function renderFieldValue(
  value: CustomFieldValue | undefined,
  definition: CustomFieldDefinition,
  t: (key: string) => string,
): React.ReactNode {
  if (!value) {
    return <span className="text-sm text-muted-foreground">--</span>;
  }

  const fieldType = value.field_type || definition.field_type;

  switch (fieldType) {
    case 'CUSTOM_FIELD_TYPE_TEXT':
      return <span className="text-sm text-foreground">{value.text_value || '--'}</span>;

    case 'CUSTOM_FIELD_TYPE_NUMBER':
      return <span className="text-sm text-foreground font-mono">{value.number_value ?? '--'}</span>;

    case 'CUSTOM_FIELD_TYPE_DATE':
    case 'CUSTOM_FIELD_TYPE_DATETIME':
      return <span className="text-sm text-foreground">{value.date_value ? formatDateTime(value.date_value) : '--'}</span>;

    case 'CUSTOM_FIELD_TYPE_SELECT': {
      const option = definition.options?.find(o => o.slug === value.select_value);
      return option ? (
        <Badge
          variant="secondary"
          size="sm"
          style={option.color ? { backgroundColor: `${option.color}20`, color: option.color, borderColor: option.color } : undefined}
        >
          {option.label}
        </Badge>
      ) : (
        <span className="text-sm text-foreground">{value.select_value || '--'}</span>
      );
    }

    case 'CUSTOM_FIELD_TYPE_MULTI_SELECT': {
      const slugs = value.multi_select_value?.slugs || [];
      if (slugs.length === 0) return <span className="text-sm text-muted-foreground">--</span>;
      return (
        <div className="flex flex-wrap gap-1">
          {slugs.map(slug => {
            const option = definition.options?.find(o => o.slug === slug);
            return (
              <Badge
                key={slug}
                variant="secondary"
                size="sm"
                style={option?.color ? { backgroundColor: `${option.color}20`, color: option.color, borderColor: option.color } : undefined}
              >
                {option?.label || slug}
              </Badge>
            );
          })}
        </div>
      );
    }

    case 'CUSTOM_FIELD_TYPE_USER':
      return (
        <span className="text-sm text-foreground">
          {value.user_value?.display_name || value.user_value?.entity?.id || '--'}
        </span>
      );

    case 'CUSTOM_FIELD_TYPE_ENTITY':
      return (
        <span className="text-sm text-primary font-mono text-xs">
          {value.entity_value?.id || '--'}
        </span>
      );

    case 'CUSTOM_FIELD_TYPE_URL':
      return value.url_value ? (
        <a href={value.url_value} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline truncate block">
          {value.url_value}
        </a>
      ) : (
        <span className="text-sm text-muted-foreground">--</span>
      );

    case 'CUSTOM_FIELD_TYPE_CHECKBOX':
      return (
        <span className={`text-sm ${value.checkbox_value ? 'text-green-600' : 'text-muted-foreground'}`}>
          {value.checkbox_value ? t('common.yes') : t('common.no')}
        </span>
      );

    default:
      return <span className="text-sm text-muted-foreground">--</span>;
  }
}

// Inline edit input for a custom field
function CustomFieldInput({
  definition,
  value,
  onChange,
}: {
  definition: CustomFieldDefinition;
  value: CustomFieldValue | undefined;
  onChange: (fieldId: string, newValue: any) => void;
}) {
  const fieldType = definition.field_type;
  const fieldId = definition.entity?.id || '';

  const inputClass = "w-full rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-ring";

  switch (fieldType) {
    case 'CUSTOM_FIELD_TYPE_TEXT':
      return (
        <input
          type="text"
          defaultValue={value?.text_value || ''}
          placeholder={definition.placeholder || ''}
          className={inputClass}
          onBlur={(e) => onChange(fieldId, { text_value: e.target.value })}
        />
      );

    case 'CUSTOM_FIELD_TYPE_NUMBER':
      return (
        <input
          type="number"
          defaultValue={value?.number_value ?? ''}
          placeholder={definition.placeholder || ''}
          className={inputClass}
          onBlur={(e) => onChange(fieldId, { number_value: parseFloat(e.target.value) || 0 })}
        />
      );

    case 'CUSTOM_FIELD_TYPE_DATE':
      return (
        <input
          type="date"
          defaultValue={value?.date_value ? String(value.date_value).substring(0, 10) : ''}
          className={inputClass}
          onChange={(e) => onChange(fieldId, { date_value: e.target.value ? new Date(e.target.value).toISOString() : null })}
        />
      );

    case 'CUSTOM_FIELD_TYPE_DATETIME':
      return (
        <input
          type="datetime-local"
          defaultValue={value?.date_value ? String(value.date_value).substring(0, 16) : ''}
          className={inputClass}
          onChange={(e) => onChange(fieldId, { date_value: e.target.value ? new Date(e.target.value).toISOString() : null })}
        />
      );

    case 'CUSTOM_FIELD_TYPE_SELECT':
      return (
        <Select value={value?.select_value || ''} onChange={(e) => onChange(fieldId, { select_value: e.target.value })}>
          <option value="">--</option>
          {(definition.options || []).map(opt => (
            <option key={opt.slug} value={opt.slug}>{opt.label}</option>
          ))}
        </Select>
      );

    case 'CUSTOM_FIELD_TYPE_CHECKBOX':
      return (
        <input
          type="checkbox"
          defaultChecked={value?.checkbox_value || false}
          className="h-4 w-4 rounded border-input"
          onChange={(e) => onChange(fieldId, { checkbox_value: e.target.checked })}
        />
      );

    case 'CUSTOM_FIELD_TYPE_URL':
      return (
        <input
          type="url"
          defaultValue={value?.url_value || ''}
          placeholder={definition.placeholder || 'https://...'}
          className={inputClass}
          onBlur={(e) => onChange(fieldId, { url_value: e.target.value })}
        />
      );

    default:
      return (
        <input
          type="text"
          defaultValue={value?.text_value || ''}
          className={inputClass}
          onBlur={(e) => onChange(fieldId, { text_value: e.target.value })}
        />
      );
  }
}

export const CustomFieldsCard = observer(({ taskId, editable = false }: CustomFieldsCardProps) => {
  const store = useTask();
  const { t } = useI18n();

  const [customFields, setCustomFields] = useState<{
    values: CustomFieldValue[];
    definitions: CustomFieldDefinition[];
  }>({ values: [], definitions: [] });
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);

  useEffect(() => {
    if (taskId) {
      setLoading(true);
      store.GetTaskCustomFields?.(taskId)
        ?.then((res: any) => {
          setCustomFields({
            values: res?.values?.values || [],
            definitions: res?.definitions?.definitions || [],
          });
        })
        ?.catch(() => {})
        ?.finally(() => setLoading(false));
    }
  }, [taskId]);

  const handleFieldChange = async (fieldId: string, valuePayload: any) => {
    try {
      await store.SetTaskCustomField?.(taskId, fieldId, { field_id: fieldId, value: valuePayload } as any);
      // Reload custom fields
      const res = await store.GetTaskCustomFields?.(taskId);
      if (res) {
        setCustomFields({
          values: res?.values?.values || [],
          definitions: res?.definitions?.definitions || [],
        });
      }
    } catch {
      // Error handled by store
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-4">
          <div className="h-4 w-24 rounded bg-muted animate-pulse mb-2" />
          <div className="h-4 w-full rounded bg-muted animate-pulse" />
        </CardContent>
      </Card>
    );
  }

  // Don't show card if no custom field definitions exist
  if (customFields.definitions.length === 0) {
    return null;
  }

  // Sort definitions by position
  const sortedDefs = [...customFields.definitions].sort((a, b) => (a.position || 0) - (b.position || 0));

  // Group by group field
  const groups = new Map<string, CustomFieldDefinition[]>();
  for (const def of sortedDefs) {
    const group = def.group || '';
    if (!groups.has(group)) groups.set(group, []);
    groups.get(group)!.push(def);
  }

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
            {t('task.customFields')}
          </p>
          {editable && (
            <button
              type="button"
              onClick={() => setEditMode(!editMode)}
              className="text-xs text-primary hover:underline"
            >
              {editMode ? t('common.done') : t('common.edit')}
            </button>
          )}
        </div>

        <div className="space-y-2.5">
          {Array.from(groups.entries()).map(([groupName, defs]) => (
            <div key={groupName || '__default'}>
              {groupName && (
                <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-1.5 mt-2">
                  {groupName}
                </p>
              )}
              {defs.map(def => {
                const fieldId = def.entity?.id || '';
                const value = customFields.values.find(v => v.field_id === fieldId);
                return (
                  <div key={fieldId} className="flex items-start justify-between gap-2">
                    <span className="text-sm text-muted-foreground shrink-0 pt-0.5">
                      {def.display_name}
                      {def.is_required && <span className="text-destructive ml-0.5">*</span>}
                    </span>
                    <div className="text-right min-w-0">
                      {editMode ? (
                        <CustomFieldInput
                          definition={def}
                          value={value}
                          onChange={handleFieldChange}
                        />
                      ) : (
                        renderFieldValue(value, def, t)
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
});
